// Live progress for the Instagram tab's "Analyze" job: progress bar, per-step checklist and a real-time log.
// Data comes from GET /api/admin/ig-content/analyze/status (state lives in MongoDB — see services/igContentAnalysis.js).
import { useEffect, useRef, useState } from "react";

const STEP_LABELS = ["Fetch video", "Whisper", "GPT", "Gemini", "Save"];
const STEP_ICON = { done: "✓", skipped: "–", error: "✕" };

export const stepsDone = (job) => (job?.stepStates || []).filter((s) => s === "done" || s === "skipped").length;

// Overall % — each finished post counts fully, the current post counts by how many of its 5 steps are done.
export function percent(job) {
  if (!job?.total) return 0;
  const finished = job.done + job.failed;
  if (!job.running) return Math.round((finished / job.total) * 100);
  const frac = job.current ? stepsDone(job) / STEP_LABELS.length : 0;
  return Math.round(Math.min(98, Math.max(4, ((finished + frac) / job.total) * 100)));
}

const clock = (ms) => `${String(Math.floor(ms / 60000)).padStart(2, "0")}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;
const hhmmss = (t) => new Date(t).toLocaleTimeString([], { hour12: false });

function useTicker(active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

function Steps({ states = [] }) {
  return (
    <div className="igc-steps">
      {STEP_LABELS.map((label, i) => {
        const st = states[i] || "pending";
        return (
          <div key={label} className={`igc-step igc-step--${st}`}>
            <span className="igc-step-dot">{st === "active" ? <span className="igc-spin igc-spin--sm" /> : STEP_ICON[st] || i + 1}</span>
            <span>{st === "skipped" ? `${label} (skipped)` : label}</span>
          </div>
        );
      })}
    </div>
  );
}

// Big panel above the table. Stays put (sticky) while you scroll, and stays after the run so you can read what happened.
export default function AnalysisPanel({ job, onDismiss }) {
  const logRef = useRef(null);
  const now = useTicker(job.running);
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [job.log?.length]);

  const pct = percent(job);
  const finished = job.done + job.failed;
  const elapsed = (job.running ? now : new Date(job.finishedAt || now).getTime()) - new Date(job.startedAt || now).getTime();
  const allFailed = !job.running && job.failed > 0 && job.done === 0;
  const tone = job.running ? "run" : allFailed ? "fail" : job.failed ? "warn" : "ok";

  return (
    <div className={`igc-panel igc-panel--${tone}`} role="status" aria-live="polite">
      <div className="igc-panel-head">
        <div className="igc-panel-title">
          {job.running ? <><span className="igc-spin" />Analyzing Reel {Math.min(finished + 1, job.total)} of {job.total}</>
            : allFailed ? <>✕ Analysis failed</> : <>✓ Finished — {job.done} analyzed{job.failed ? `, ${job.failed} failed` : ""}</>}
        </div>
        <div className="igc-panel-meta">
          <span>{clock(Math.max(0, elapsed))}</span>
          {!job.running && <button className="igc-x" onClick={onDismiss} aria-label="Dismiss">Dismiss</button>}
        </div>
      </div>

      <div className="igc-bar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} role="progressbar">
        <div className={`igc-bar-fill ${job.running ? "igc-bar-fill--live" : ""} igc-bar-fill--${tone}`} style={{ width: `${Math.max(pct, 2)}%` }} />
        <span className="igc-bar-pct">{pct}%</span>
      </div>

      {job.running && job.currentCaption && <div className="igc-now">Now: <b>“{job.currentCaption}”</b></div>}
      {job.running && <Steps states={job.stepStates} />}
      {job.running && <div className="igc-hint">Live — you can scroll, open other tabs or leave this page; it keeps running on the server.</div>}

      <div className="igc-log" ref={logRef} aria-label="Live activity log">
        {(job.log || []).length === 0 ? <div className="igc-log-line igc-log--info">Waiting for the first update…</div>
          : job.log.map((l, i) => (
            <div key={`${l.t}-${i}`} className={`igc-log-line igc-log--${l.level || "info"}`}><span className="igc-log-t">{hhmmss(l.t)}</span>{l.msg}</div>
          ))}
        {job.running && <div className="igc-log-line igc-log--cursor"><span className="igc-log-t">{hhmmss(now)}</span><span className="igc-dots">working</span></div>}
      </div>
    </div>
  );
}

// Compact version shown inside a post's ▾ details while that post is being analyzed.
export function InlineProgress({ job }) {
  const last = (job.log || []).filter((l) => !job.current || l.mediaId === job.current).slice(-4);
  return (
    <div className="igc-inline">
      <div className="igc-inline-top"><span className="igc-spin" /><b>Analyzing this Reel…</b><span className="muted">{stepsDone(job)} of {STEP_LABELS.length} steps</span></div>
      <div className="igc-bar igc-bar--sm"><div className="igc-bar-fill igc-bar-fill--live igc-bar-fill--run" style={{ width: `${Math.max(6, (stepsDone(job) / STEP_LABELS.length) * 100)}%` }} /></div>
      <Steps states={job.stepStates} />
      <div className="igc-log igc-log--mini">
        {last.map((l, i) => <div key={i} className={`igc-log-line igc-log--${l.level || "info"}`}><span className="igc-log-t">{hhmmss(l.t)}</span>{l.msg}</div>)}
      </div>
    </div>
  );
}
